import { type FileNode, fileCategory } from '@xfile/shared';

/**
 * Règle d'inclusion en GALERIE / visionneuse (XFILE-26 ; extrait de `StorageBrowser` en XFILE-29
 * pour être partagé par la page PUBLIQUE d'un dossier partagé SANS tirer tout `StorageBrowser` dans
 * le bundle public). Helper PUR (testé), réutilisé à l'identique côté authentifié et public.
 */

/**
 * Jetons (sous-type MIME OU extension, sans point, minuscule) des images NATIVEMENT affichables
 * par un navigateur : elles se rendent sans vignette. Tout autre format image (HEIC/HEIF…) ne se
 * rend PAS brut et n'apparaît en galerie / ne s'affiche dans la visionneuse QUE via sa vignette WebP.
 */
const BROWSER_NATIVE_IMAGE_TOKENS: ReadonlySet<string> = new Set([
  'jpeg',
  'jpg',
  'png',
  'gif',
  'webp',
  'avif',
  'svg',
]);

/** Extension (sans point, en minuscules) d'un nom de fichier, ou `null` si absente. */
function extensionOf(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return null;
  return name.slice(dot + 1).toLowerCase();
}

/**
 * L'image est-elle NATIVEMENT affichable par un navigateur (jpeg/jpg/png/gif/webp/avif/svg) ?
 * Testé par le SOUS-TYPE MIME (`image/<sous-type>`, `svg+xml` ramené à `svg`) OU par l'EXTENSION du
 * nom. Les formats hors liste (HEIC/HEIF…) ne se rendent pas bruts et exigent une vignette.
 */
function isBrowserNativeImage(mimeType: string, name: string): boolean {
  const mime = mimeType.toLowerCase();
  if (mime.startsWith('image/')) {
    const subtype = mime.slice('image/'.length);
    const token = subtype === 'svg+xml' ? 'svg' : subtype;
    if (BROWSER_NATIVE_IMAGE_TOKENS.has(token)) return true;
  }
  const ext = extensionOf(name);
  return ext !== null && BROWSER_NATIVE_IMAGE_TOKENS.has(ext);
}

/**
 * Une image doit-elle APPARAÎTRE dans la grille GALERIE (et se rendre comme image dans la
 * visionneuse) ? VRAI ssi le fichier a du contenu, est de catégorie image (`fileCategory`), ET est
 * soit nativement affichable (jpeg/png/gif/webp/avif/svg) soit pourvu d'une vignette
 * (`hasThumbnail`). Ainsi un HEIC décodé en WebP (vignette présente) apparaît VIA sa vignette ; un
 * HEIC non décodable est exclu PROPREMENT (jamais de tuile cassée). Helper PUR (testé, XFILE-26).
 *
 * Côté PUBLIC (dossier partagé `/d/:slug`), les fichiers listés n'ont pas de champ `hasContent`
 * (ce sont des fichiers ACTIFS) : l'appelant passe `hasContent: true`.
 */
export function isGalleryDisplayable(
  file: Pick<FileNode, 'mimeType' | 'name' | 'hasContent' | 'hasThumbnail'>,
): boolean {
  if (!file.hasContent) return false;
  if (fileCategory(file.mimeType, file.name) !== 'image') return false;
  if (isBrowserNativeImage(file.mimeType, file.name)) return true;
  return file.hasThumbnail === true;
}
